import { useQuery } from '@tanstack/react-query'

import { axiosInstance } from '../helpers'

export interface PersonServiceRequestWorkorder {
    id: number
    status: string | null
    title?: string | null
    assigned_to?: {
        id: number
        name: string
    } | null
}

export interface PersonServiceRequest {
    id: number
    lease?: number | null
    lease_id?: number | null
    issue_title: string | null
    description: string
    status: string
    category: string
    priority?: string | null
    created_date: string
    unit_name?: string | null
    folder_name?: string | null
    path?: string | null
    workorder?: PersonServiceRequestWorkorder | null
}

export interface PersonServiceRequestGroup {
    leaseId: number
    label: string
    requests: PersonServiceRequest[]
}

export function resolvePersonServiceRequestLeaseId(
    row: PersonServiceRequest,
): number {
    return row.lease ?? row.lease_id ?? 0
}

export function personServiceRequestLeaseLabel(
    row: PersonServiceRequest,
): string {
    const unit = (row.unit_name || '').trim()
    const folder = (row.folder_name || '').trim()
    if (unit && folder) {
        return `${folder} · ${unit}`
    }
    if (unit) {
        return unit
    }
    if (folder) {
        return folder
    }
    const leaseId = resolvePersonServiceRequestLeaseId(row)
    return leaseId > 0 ? `Lease ${leaseId}` : 'Unknown unit'
}

export function groupPersonServiceRequests(
    requests: PersonServiceRequest[],
): PersonServiceRequestGroup[] {
    const groups = new Map<number, PersonServiceRequestGroup>()
    const order: number[] = []
    for (const row of requests) {
        const leaseId = resolvePersonServiceRequestLeaseId(row)
        let group = groups.get(leaseId)
        if (!group) {
            group = {
                leaseId,
                label: personServiceRequestLeaseLabel(row),
                requests: [],
            }
            groups.set(leaseId, group)
            order.push(leaseId)
        }
        group.requests.push(row)
    }
    return order.map((leaseId) => groups.get(leaseId)!)
}

export function usePersonServiceRequests(
    userId: number | null | undefined,
    enabled: boolean,
) {
    const query = useQuery<PersonServiceRequest[], Error>({
        queryKey: ['person-service-requests', userId],
        queryFn: async () => {
            const res = await axiosInstance.get<PersonServiceRequest[]>(
                'service-request/apartment_service_request/',
                { params: { user_id: userId } },
            )
            return Array.isArray(res.data) ? res.data : []
        },
        enabled: enabled && userId != null && userId > 0,
        refetchOnWindowFocus: false,
    })

    const requests = query.data ?? []
    return {
        requests,
        groups: groupPersonServiceRequests(requests),
        isLoading: query.isLoading,
    }
}
